import { Component, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import {
  EnergyWalletMovementTypeValues,
  EnergyWalletMovementTypes,
  IEnergyWalletBalanceDTO,
  IEnergyWalletCreditLot,
  IEnergyWalletMovement,
} from 'common_library';
import { EnergyWalletService } from 'src/app/services/entities/energy-wallet.service';

type RefreshEvent = CustomEvent & {
  target: EventTarget & {
    complete?: () => Promise<void> | void;
  };
};

@Component({
  selector: 'app-energy-wallet-details',
  templateUrl: './energy-wallet-details.component.html',
  styleUrls: ['./energy-wallet-details.component.scss'],
  standalone: false,
})
export class EnergyWalletDetailsComponent implements OnInit {
  wallet?: IEnergyWalletBalanceDTO | null;
  movements: IEnergyWalletMovement[] = [];
  loading = true;
  error = false;

  constructor(
    private modalCtrl: ModalController,
    private energyWalletService: EnergyWalletService,
    private translate: TranslateService,
  ) {}

  async ngOnInit() {
    await this.reload();
  }

  async reload(event?: RefreshEvent) {
    this.error = false;
    this.loading = !event;

    try {
      const [wallet, movementPage] = await Promise.all([
        this.energyWalletService.getMine(),
        this.energyWalletService.getMyMovements(0, '', 100),
      ]);

      this.wallet = wallet;
      this.movements = movementPage?.data ?? wallet?.recentMovements ?? [];
    } catch (error) {
      this.wallet = null;
      this.movements = [];
      this.error = true;
    } finally {
      this.loading = false;
      await event?.target?.complete?.();
    }
  }

  closeMe() {
    this.modalCtrl.dismiss();
  }

  formatKwh(valueWh?: number): string {
    const kwh = Number(valueWh || 0) / 1000;
    return `${kwh.toFixed(2)} kWh`;
  }

  formatSignedKwh(valueWh?: number): string {
    const amount = Number(valueWh || 0);
    const sign = amount > 0 ? '+' : '';
    return `${sign}${this.formatKwh(amount)}`;
  }

  sourceTypeLabel(sourceType?: string): string {
    if (!sourceType) return '-';
    return this.translate.instant(`ENERGY_WALLET.SOURCE.${sourceType}`);
  }

  movementTranslationKey(type?: EnergyWalletMovementTypeValues): string {
    return `ENERGY_WALLET.MOVEMENT.${type || 'UNKNOWN'}`;
  }

  movementColor(type?: EnergyWalletMovementTypeValues): string {
    switch (type) {
      case EnergyWalletMovementTypes.LOAD:
        return 'success';
      case EnergyWalletMovementTypes.UNLOAD:
        return 'primary';
      case EnergyWalletMovementTypes.HOLD:
        return 'warning';
      case EnergyWalletMovementTypes.REVERSAL:
      case EnergyWalletMovementTypes.ADJUSTMENT:
        return 'tertiary';
      case EnergyWalletMovementTypes.EXPIRE:
        return 'danger';
      default:
        return 'medium';
    }
  }

  movementReason(movement: IEnergyWalletMovement): string {
    if (movement?.type === EnergyWalletMovementTypes.UNLOAD && movement?.cdrId) {
      return this.translate.instant('ENERGY_WALLET.MOVEMENT_REASON.UNLOAD_CDR', { cdrId: movement.cdrId });
    }
    return movement?.reason || '-';
  }

  creditLotPayerLabel(lot: IEnergyWalletCreditLot): string {
    if (lot.payerCompany?.legalName) {
      return lot.payerCompany.legalName;
    }

    if (lot.payerCompanyId) {
      return this.translate.instant('ENERGY_WALLET.PAYER_COMPANY_ID', { id: lot.payerCompanyId });
    }

    if (lot.payerUserId) {
      return this.translate.instant('ENERGY_WALLET.PAYER_USER_ID', { id: lot.payerUserId });
    }

    return this.sourceTypeLabel(lot.sourceType);
  }

  trackLot(_: number, lot: IEnergyWalletCreditLot) {
    return lot.id;
  }

  trackMovement(_: number, movement: IEnergyWalletMovement) {
    return movement.id;
  }
}
